'use client';

import { FC, useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { EditExpenseForm } from '../edit/edit-expense-form';
import { useExpenseContext } from '@/contexts/expense-context';
import { getExpenses } from '@/actions';
import { ExpenseForEdit } from '@/types';

export const EditExpensePopup: FC = () => {
  const { dispatch, state } = useExpenseContext();
  const [expenses, setExpenses] = useState<
    Awaited<ReturnType<typeof getExpenses>>
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const data = await getExpenses();
        setExpenses(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching expenses:', error);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };

    if (state.isEditExpensePopupOpen) {
      setLoading(true);
      fetchExpenses();
    }
  }, [state.isEditExpensePopupOpen]);

  const expense = expenses.find((item) => item.id === state.expenseId);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && state.expenseId) {
      dispatch({
        type: 'OPEN_EDIT_EXPENSE_POPUP',
        payload: { expenseId: state.expenseId },
      });
    } else {
      dispatch({ type: 'CLOSE_EDIT_EXPENSE_POPUP' });
    }
  };

  if (loading) {
    return (
      <Sheet
        open={state.isEditExpensePopupOpen}
        onOpenChange={handleOpenChange}
      >
        <SheetContent className="bg-white dark:bg-black">
          <SheetHeader>
            <SheetTitle>Edit expense</SheetTitle>
            <SheetDescription>Loading expense details...</SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!expense) {
    return (
      <Sheet
        open={state.isEditExpensePopupOpen}
        onOpenChange={handleOpenChange}
      >
        <SheetContent className="bg-white dark:bg-black">
          <SheetHeader>
            <SheetTitle>Edit expense</SheetTitle>
            <SheetDescription>Expense could not be found.</SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-destructive">
              Error: Expense not found
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={state.isEditExpensePopupOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="bg-white dark:bg-black">
        <SheetHeader>
          <SheetTitle className="text-left">Edit expense</SheetTitle>
          <SheetDescription>Update the expense details.</SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 p-4">
          <EditExpenseForm expense={expense as unknown as ExpenseForEdit} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
